import { forwardRef, useImperativeHandle, ForwardRefRenderFunction, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AgxColumn, Images, AgxButton } from '@urbanx/agx-ui-components';
import { useSelectedAgency, useSelectedCampaign, useSelectedContract } from 'hooks';
import PlaceHolderImage from 'components/placeholder-image/PlaceholderImage';
import { Pages } from 'constants/menu';
import { cleanTwoLineAddress } from 'helpers/address';
import './PageHeader.scss';
import BackButtonBreadCrumbs from './components/BackButtonBreadCrumbs';
import PageLevelSearch from 'components/PageLevelSearch/PageLevelSearch';
import {
  AgxAvatar,
  AgxRow,
  AgxSpacer,
  AgxTypography,
  Breakpoints,
  useWindowSize,
} from '@urbanx/agx.ui-components2';

type PageHeaderHandle = {
  closeMenuIfOpen: () => void;
};
export interface PageHeaderProps {
  primaryPageTitle: string;
  agentName: string;
  isDashboard?: boolean;
  isAgencyDashboard?: boolean;
  isStaff?: boolean;
  isCampaignsSection?: boolean;
  isError?: boolean;
  agentImgUrl?: string;
  currentPageTitle?: string;
  hideSearch?: boolean;
  pageIcon?: React.ReactNode;
}

const PageHeader: ForwardRefRenderFunction<PageHeaderHandle, PageHeaderProps> = (props, ref) => {
  const {
    primaryPageTitle,
    agentName,
    currentPageTitle,
    isDashboard,
    isAgencyDashboard,
    isError,
    agentImgUrl,
    isCampaignsSection,
    hideSearch = false,
    pageIcon,
  } = props;

  const [showMenu, setShowMenu] = useState(false);
  const navigate = useNavigate();
  const { agencyId, campaignId } = useParams();
  const { agency: selectedAgency } = useSelectedAgency();
  const { campaign: selectedCampaign } = useSelectedCampaign();
  const { contract: selectedContract } = useSelectedContract();
  const imgUrl = isDashboard
    ? agentImgUrl
    : selectedAgency?.agency?.settings?.branding?.logo?.publicPath;

  const [addressLineOne] = cleanTwoLineAddress(selectedCampaign?.address);

  useImperativeHandle(ref, () => ({
    closeMenuIfOpen() {
      if (showMenu) setShowMenu(false);
    },
  }));

  const onBackClick = () => {
    if (selectedContract) {
      navigate(
        `/${Pages.AGENCIES}/${agencyId}/${Pages.CAMPAIGNS}/${campaignId}/${Pages.CONTRACTS}`
      );
    } else if (campaignId) {
      navigate(`/${Pages.AGENCIES}/${agencyId}/${Pages.CAMPAIGNS}`);
    } else if (isAgencyDashboard) {
      navigate(`/partners`);
    } else if (agencyId) {
      navigate(`/${Pages.AGENCIES}`);
    } else {
      navigate('/');
    }
  };

  const displayBreadcrumbs = agencyId || campaignId;

  const windowSize = useWindowSize();

  if (isError) {
    return (
      <div className="headerContaineronError">
        <section className="titleBar">
          <div onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <Images.UrbanXWhite />
          </div>
          {imgUrl ? (
            <img className="titleLogo" src={imgUrl} />
          ) : (
            <PlaceHolderImage size={32} title={agentName} />
          )}
        </section>
      </div>
    );
  }

  return (
    <AgxColumn extraClasses={isDashboard ? '' : 'headerRow'}>
      <div className="globalSearchContainer">{!hideSearch && <PageLevelSearch />}</div>
      {(displayBreadcrumbs || (isCampaignsSection && !hideSearch)) && (
        <AgxRow
          fill
          gap="spacing-md"
          justifyContent={!displayBreadcrumbs ? 'right' : 'space-between'}
          padding={!displayBreadcrumbs ? 'spacing-lg' : undefined}
          alignItems="alignCenter"
        >
          {displayBreadcrumbs && (
            <AgxButton
              id="btn-back"
              extraClasses="breadcrumbHeaderButton"
              leftIcon={<Images.ArrowBackOutline />}
              naked
              text={
                <BackButtonBreadCrumbs
                  primaryPageTitle={primaryPageTitle}
                  addressLineOne={addressLineOne}
                  agencyId={agencyId}
                  campaignId={campaignId}
                  isAgencyDashboard={isAgencyDashboard}
                  selectedContract={selectedContract}
                />
              }
              onClick={() => onBackClick()}
            />
          )}
        </AgxRow>
      )}
      {!isCampaignsSection ? (
        <section>
          <AgxSpacer
            direction={windowSize === Breakpoints.Mobile ? 'vertical' : 'horizontal'}
            contentAlignment="center"
            contentJustification="space-between"
            gap="sm"
          >
            <AgxSpacer fill direction="horizontal" gap="sm" contentAlignment="center">
              {primaryPageTitle === 'URBAN X' ? (
                <AgxColumn extraClasses="paddingRight2">
                  <Images.RealEstateLogo />
                </AgxColumn>
              ) : !isDashboard ? (
                imgUrl ? (
                  <AgxAvatar
                    type="agency-brand"
                    size="lg"
                    style={{
                      background: selectedAgency?.agency?.settings?.branding?.backgroundColor,
                    }}
                    icon={<img src={imgUrl} />}
                  />
                ) : pageIcon !== undefined ? (
                  pageIcon
                ) : (
                  <PlaceHolderImage size={48} title={isDashboard ? agentName : primaryPageTitle} />
                )
              ) : null}
              <AgxSpacer direction="horizontal" gap={'xs'}>
                <AgxTypography
                  id={'txtprimaryPageTitle'}
                  type={'heading'}
                  size={'2x-large'}
                  color="var(--text-primary)"
                >
                  {isDashboard ? `` : primaryPageTitle}{' '}
                </AgxTypography>
                {currentPageTitle && (
                  <AgxTypography
                    id={'txtprimaryPageTitle'}
                    type={'heading'}
                    size={'2x-large'}
                    color="var(--text-tertiary)"
                  >
                    {`/ ${currentPageTitle}`}
                  </AgxTypography>
                )}
              </AgxSpacer>
            </AgxSpacer>
          </AgxSpacer>
        </section>
      ) : (
        <></>
      )}
    </AgxColumn>
  );
};

export default forwardRef(PageHeader);
